import { useCallback, useEffect, useMemo, useState, type KeyboardEvent } from 'react'
import { Draggable, type DropResult } from 'react-beautiful-dnd'

import { F0Box, F0Button, F0Heading, F0Icon } from '@factorialco/f0-react'
import { Input, NumberInput } from '@factorialco/f0-react/dist/experimental'
import { ChevronDown, ChevronUp } from '@factorialco/f0-react/icons/app'
import { CollapsableCard } from '@factorialco/deprecated-design-system'

import { DragAndDropContext, DropableBox } from 'modules/core/components/DragAndDrop'
import i18n from 'modules/core/lib/i18n'

import AddFieldModal from './_AddFieldModal'
import SectionField from './SectionField'
import type { NumerationErrors, NumerationState } from './index'
import { sectionLabel } from './translations'
import { SECTION_ORDER, toSectionType, type MergedField, type SectionType } from './types'

type OpenPanel = SectionType | 'numeration' | null

/**
 * Pure handler for react-beautiful-dnd's onDragEnd. Exported for unit testing.
 * Reorders fields only when the drag stays within the same section AND the index changed.
 */
export function handleFieldDragEnd(
  result: DropResult,
  onReorderFields: (section: SectionType, sourceIndex: number, destinationIndex: number) => void
): void {
  const { source, destination } = result
  if (!destination) return
  // Reorder is restricted to the same section.
  if (source.droppableId !== destination.droppableId) return
  if (source.index === destination.index) return
  onReorderFields(toSectionType(source.droppableId), source.index, destination.index)
}

type Props = {
  fields: MergedField[]
  numeration: NumerationState
  numerationErrors?: NumerationErrors
  onToggleVisibility: (fieldKey: string, newVisible: boolean) => void
  onToggleMandatory: (fieldKey: string, newRequired: boolean) => void
  onAddCustomField: (
    section: SectionType,
    label: string,
    fieldType: 'text' | 'select',
    options: string[]
  ) => void
  onEditCustomField: (key: string, label: string, options: string[]) => void
  onDeleteCustomField: (key: string) => void
  onReorderFields: (section: SectionType, sourceIndex: number, destinationIndex: number) => void
  onNumerationChange: (numeration: NumerationState) => void
}

function SideContent({
  fields,
  numeration,
  numerationErrors,
  onToggleVisibility,
  onToggleMandatory,
  onAddCustomField,
  onEditCustomField,
  onDeleteCustomField,
  onReorderFields,
  onNumerationChange,
}: Props) {
  const [openPanel, setOpenPanel] = useState<OpenPanel>('numeration')
  const [addingSection, setAddingSection] = useState<SectionType | null>(null)
  const [editingField, setEditingField] = useState<MergedField | null>(null)

  const hasNumerationError = Boolean(numerationErrors?.prefix || numerationErrors?.nextNumber)

  useEffect(() => {
    if (hasNumerationError) setOpenPanel('numeration')
  }, [hasNumerationError])

  const fieldsBySection = useMemo(() => {
    const grouped = new Map<SectionType, MergedField[]>()
    for (const section of SECTION_ORDER) {
      grouped.set(section, [])
    }
    for (const field of fields) {
      const sectionFields = grouped.get(field.sectionType)
      if (sectionFields) {
        sectionFields.push(field)
      }
    }
    for (const sectionFields of grouped.values()) {
      sectionFields.sort((a, b) => a.position - b.position)
    }
    return grouped
  }, [fields])

  const togglePanel = useCallback((panel: OpenPanel) => {
    setOpenPanel((prev) => (prev === panel ? null : panel))
  }, [])

  const handleHeaderKeyDown = useCallback(
    (panel: OpenPanel) => (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        togglePanel(panel)
      }
    },
    [togglePanel]
  )

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      handleFieldDragEnd(result, onReorderFields)
    },
    [onReorderFields]
  )

  return (
    <DragAndDropContext onDragEnd={handleDragEnd}>
      <F0Box display="flex" flexDirection="column" gap="md" padding="md">
        <CollapsableCard
          open={openPanel === 'numeration'}
          contentPadding="s0"
          contentBackground="grey000White"
          Card={
            <F0Box
              onClick={() => togglePanel('numeration')}
              onKeyDown={handleHeaderKeyDown('numeration')}
              role="button"
              tabIndex={0}
              aria-expanded={openPanel === 'numeration'}
              paddingX="sm"
            >
              <F0Box display="flex" alignItems="center" justifyContent="between" width="full">
                <F0Heading
                  content={i18n.t(
                    'procurement.purchase_requests.settings.po_templates.detail.numeration'
                  )}
                  variant="heading"
                />
                <F0Icon icon={openPanel === 'numeration' ? ChevronUp : ChevronDown} size="md" />
              </F0Box>
            </F0Box>
          }
          Content={
            <F0Box display="flex" flexDirection="column" gap="md" padding="md">
              <Input
                label={i18n.t(
                  'procurement.purchase_requests.settings.po_templates.detail.numeration_prefix'
                )}
                value={numeration.prefix}
                onChange={(value) => onNumerationChange({ ...numeration, prefix: value })}
                placeholder="PO"
                required
                error={numerationErrors?.prefix}
              />
              <NumberInput
                label={i18n.t(
                  'procurement.purchase_requests.settings.po_templates.detail.numeration_next_number'
                )}
                value={numeration.nextNumber}
                onChange={(value) =>
                  onNumerationChange({ ...numeration, nextNumber: value ?? null })
                }
                placeholder="1"
                required
                error={numerationErrors?.nextNumber}
                locale={i18n.locale}
              />
            </F0Box>
          }
        />

        {SECTION_ORDER.map((section) => {
          const sectionFields = fieldsBySection.get(section) ?? []
          const isOpen = openPanel === section

          return (
            <CollapsableCard
              key={section}
              open={isOpen}
              contentPadding="s0"
              contentBackground="grey000White"
              Card={
                <F0Box
                  onClick={() => togglePanel(section)}
                  onKeyDown={handleHeaderKeyDown(section)}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isOpen}
                  paddingX="sm"
                >
                  <F0Box display="flex" alignItems="center" justifyContent="between" width="full">
                    <F0Heading content={sectionLabel(section)} variant="heading" />
                    <F0Icon icon={isOpen ? ChevronUp : ChevronDown} size="md" />
                  </F0Box>
                </F0Box>
              }
              Content={
                <F0Box display="flex" flexDirection="column">
                  <DropableBox droppableId={section} type="po-template-field">
                    {sectionFields.map((field, index) => (
                      <Draggable
                        key={field.key}
                        draggableId={`po-field-${field.key}`}
                        index={index}
                      >
                        {(provided) => (
                          <div ref={provided.innerRef} {...provided.draggableProps}>
                            <SectionField
                              field={field}
                              dragHandleProps={provided.dragHandleProps}
                              onToggleVisibility={onToggleVisibility}
                              onToggleMandatory={onToggleMandatory}
                              onEdit={setEditingField}
                              onDelete={onDeleteCustomField}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                  </DropableBox>

                  {section !== 'notes_and_delivery' && (
                    <F0Box padding="md">
                      <F0Button
                        label={i18n.t(
                          'procurement.purchase_requests.settings.po_templates.detail.add_field'
                        )}
                        variant="outline"
                        size="sm"
                        onClick={() => setAddingSection(section)}
                      />
                    </F0Box>
                  )}
                </F0Box>
              }
            />
          )
        })}

        {addingSection && (
          <AddFieldModal
            section={addingSection}
            onClose={() => setAddingSection(null)}
            onAdd={onAddCustomField}
          />
        )}

        {editingField && (
          <AddFieldModal
            section={editingField.sectionType}
            editingField={editingField}
            onClose={() => setEditingField(null)}
            onAdd={onAddCustomField}
            onEdit={onEditCustomField}
          />
        )}
      </F0Box>
    </DragAndDropContext>
  )
}

export default SideContent
